import { Upload, X, ImagePlus, Watch } from "lucide-react";

const boxCls = "bg-[#0a0a0a] border border-[#262626] p-4 rounded-sm";
const pickerCls =
  "flex items-center gap-3 cursor-pointer border border-dashed border-[#404040] hover:border-white p-4 rounded-sm text-[#a3a3a3] hover:text-white transition-colors";
const labelCls = "block text-xs uppercase tracking-widest text-[#737373] mb-2";

export function VideoPicker({ file, setFile, uploading, uploadProgress }) {
  return (
    <div className={boxCls}>
      <label className={labelCls}>Highlight video (optional)</label>
      {!file ? (
        <label data-testid="create-video-picker" className={pickerCls}>
          <Upload className="w-5 h-5" />
          <span className="text-sm">Choose a video file (mp4, mov, webm) — max 200MB</span>
          <input
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>
      ) : (
        <div className="flex items-center justify-between gap-3 border border-[#262626] p-3 rounded-sm">
          <div className="text-sm text-white truncate">{file.name}</div>
          <button
            type="button"
            data-testid="create-video-clear"
            onClick={() => setFile(null)}
            className="text-[#a3a3a3] hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {uploading && file && (
        <div className="mt-3">
          <div className="w-full h-1 bg-[#262626] rounded-sm overflow-hidden">
            <div
              data-testid="create-progress-bar"
              className="h-full bg-white transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <div className="text-xs text-[#737373] mt-1">Uploading {uploadProgress}%…</div>
        </div>
      )}
    </div>
  );
}

export function PhotoPicker({ photos, setPhotos }) {
  return (
    <div className={boxCls}>
      <label className={labelCls}>Match photos (optional, up to 4)</label>
      {photos.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-3">
          {photos.map((p, i) => (
            <div key={i} className="relative group">
              <img
                src={URL.createObjectURL(p)}
                alt={`Selected ${i + 1}`}
                className="h-20 w-full object-cover border border-[#262626] rounded-sm"
              />
              <button
                type="button"
                data-testid={`create-photo-remove-${i}`}
                onClick={() => setPhotos((ps) => ps.filter((_, x) => x !== i))}
                className="absolute top-1 right-1 bg-black/80 border border-[#404040] text-white p-0.5 rounded-sm hover:bg-black"
                aria-label="Remove photo"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {photos.length < 4 && (
        <label data-testid="create-photo-picker" className={pickerCls}>
          <ImagePlus className="w-5 h-5" />
          <span className="text-sm">Add photos (jpg, png)</span>
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const picked = Array.from(e.target.files || []);
              setPhotos((ps) => [...ps, ...picked].slice(0, 4));
              e.target.value = "";
            }}
          />
        </label>
      )}
    </div>
  );
}

export function DeviceImport({ device, onImport, onClear }) {
  return (
    <div className={boxCls}>
      <label className={labelCls}>Tracker data (optional)</label>
      {device.stats ? (
        <div
          data-testid="device-stats-preview"
          className="flex items-start justify-between gap-3 border border-[#404040] p-3 rounded-sm"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm text-white">
              <Watch className="w-4 h-4 shrink-0" />
              <span className="truncate">{device.fileName}</span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
              {Object.entries(device.stats).map(([k, v]) => (
                <span key={k} className="text-xs text-[#a3a3a3]">
                  <span className="font-display text-base text-white mr-1">{v}</span>
                  {k.replaceAll("_", " ")}
                </span>
              ))}
            </div>
          </div>
          <button
            type="button"
            data-testid="device-stats-clear"
            onClick={onClear}
            className="text-[#a3a3a3] hover:text-white shrink-0"
            aria-label="Remove tracker data"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label data-testid="device-import-picker" className={pickerCls}>
          <Watch className="w-5 h-5" />
          <span className="text-sm">
            {device.loading
              ? "Reading tracker file…"
              : "Import from your tracking device (.csv or .json export)"}
          </span>
          <input
            type="file"
            accept=".csv,.json,text/csv,application/json"
            className="hidden"
            disabled={device.loading}
            onChange={(e) => {
              onImport(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
      )}
      <p className="text-[11px] text-[#737373] mt-2">
        Works with GPS vest / smartwatch exports — distance, top speed, sprints, heart rate,
        calories and minutes are picked up automatically.
      </p>
    </div>
  );
}
